// src/pages/Trend/components/analysis/ingredient-analysis/OccurrenceChart.js
import React, { useState, useCallback, memo } from 'react';
import { Search, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { ErrorMessage } from '../../../../../components/common/UI';
import { BBC_STYLE } from '../../../../../styles/chartStyles';
import './OccurrenceChart.css';

// Render filter label with monospace number for alignment
const renderFilterLabel = (label) => {
  const match = label.match(/^(\d+)(.*)$/);
  if (match) {
    return (
      <>
        <span className="filter-label-number">{match[1]}</span>
        {match[2]}
      </>
    );
  }
  return label;
};

const processChartData = (data) => {
  if (!data?.co_occurrence_metrics) return { nodes: [], links: [] };
  return data.co_occurrence_metrics;
};

const categoryColors = {
  // Korean categories
  "비타민류": "#FF9800",
  "세라마이드류": "#4FC3F7", 
  "산류": "#FF5252",
  "추출물류": "#66BB6A",
  "발효성분류": "#AB47BC",
  "단백질류": "#FFA726",
  "식물성분류": "#26A69A",
  "오일류": "#29B6F6",
  "레티놀류": "#EF5350",
  "펩타이드류": "#7E57C2",
  "미네랄류": "#78909C",
  "다당류": "#8D6E63",
  "미백성분류": "#B3E5FC",
  "복합성분류": "#90A4AE",
  "해양성분류": "#00ACC1",
  "전통성분류": "#D4A065",
  "특수성분류": "#9575CD",
  "other": "#BDBDBD",
  
  // English categories (same colors as Korean equivalents)
  "Vitamins": "#FF9800",                    // = 비타민류
  "Ceramides": "#4FC3F7",                   // = 세라마이드류  
  "acid": "#FF5252",                        // = 산류
  "Acids": "#FF5252",                       // Alternative for 산류
  "Extracts": "#66BB6A",                    // = 추출물류
  "Fermentability classification": "#AB47BC", // = 발효성분류
  "Fermented ingredients": "#AB47BC",       // Alternative for 발효성분류
  "Proteins": "#FFA726",                    // = 단백질류
  "Plant classification": "#26A69A",        // = 식물성분류
  "Plant ingredients": "#26A69A",           // Alternative for 식물성분류
  "Oils": "#29B6F6",                        // = 오일류
  "retinols": "#EF5350",                    // = 레티놀류
  "Retinols": "#EF5350",                    // = 레티놀류 (capitalized)
  "Peptides": "#7E57C2",                    // = 펩타이드류
  "Minerals": "#78909C",                    // = 미네랄류
  "polysaccharides": "#8D6E63",             // = 다당류
  "Polysaccharides": "#8D6E63",             // = 다당류 (capitalized)
  "Whitening ingredients": "#B3E5FC",       // = 미백성분류
  "Complex ingredients": "#90A4AE",         // = 복합성분류
  "Marine ingredients": "#00ACC1",          // = 해양성분류
  "Traditional ingredients": "#D4A065",     // = 전통성분류
  "Special ingredients": "#9575CD",         // = 특수성분류
  "Other": "#BDBDBD",                       // = other (capitalized)

  // 🔥 NEW: Vietnamese categories (same colors as Korean/English equivalents)
  "Vitamin": "#FF9800",                     // = 비타민류/Vitamins
  "Ceramide": "#4FC3F7",                    // = 세라마이드류/Ceramides
  "Axit": "#FF5252",                        // = 산류/Acids
  "Chiết xuất": "#66BB6A",                  // = 추출물류/Extracts
  "Thành phần lên men": "#AB47BC",          // = 발효성분류/Fermented ingredients
  "Protein": "#FFA726",                     // = 단백질류/Proteins
  "Thành phần thực vật": "#26A69A",         // = 식물성분류/Plant ingredients
  "Dầu": "#29B6F6",                         // = 오일류/Oils
  "Retinol": "#EF5350",                     // = 레티놀류/Retinols
  "Peptide": "#7E57C2",                     // = 펩타이드류/Peptides
  "Khoáng chất": "#78909C",                 // = 미네랄류/Minerals
  "Polysaccharide": "#8D6E63",              // = 다당류/Polysaccharides
  "Thành phần làm trắng": "#B3E5FC",        // = 미백성분류/Whitening ingredients
  "Thành phần phức hợp": "#90A4AE",         // = 복합성분류/Complex ingredients
  "Thành phần biển": "#00ACC1",             // = 해양성분류/Marine ingredients
  "Thành phần truyền thống": "#D4A065",     // = 전통성분류/Traditional ingredients
  "Thành phần đặc biệt": "#9575CD",         // = 특수성분류/Special ingredients
  "Khác": "#BDBDBD",                        // = other/Other
};

// Add a helper function to get color safely
const getCategoryColor = (category) => {
  if (!category) return categoryColors["other"];
  
  // Try exact match first
  if (categoryColors[category]) {
    return categoryColors[category];
  }
  
  // Try lowercase match
  const lowerCategory = category.toLowerCase();
  if (categoryColors[lowerCategory]) {
    return categoryColors[lowerCategory];
  }
  
  // Try some common mappings for English
  const englishMappings = {
    "acids": categoryColors["acid"],
    "vitamin": categoryColors["Vitamins"],
    "extract": categoryColors["Extracts"],
    "fermented": categoryColors["Fermented ingredients"],
    "plant": categoryColors["Plant ingredients"],
    "oil": categoryColors["Oils"],
    "retinol": categoryColors["Retinols"],
    "peptide": categoryColors["Peptides"],
    "mineral": categoryColors["Minerals"],
    "polysaccharide": categoryColors["Polysaccharides"],
  };
  
  // Try Vietnamese mappings
  const vietnameseMappings = {
    "axit": categoryColors["Axit"],
    "chiết xuất": categoryColors["Chiết xuất"],
    "chiet xuat": categoryColors["Chiết xuất"],
    "thành phần": categoryColors["Thành phần phức hợp"], // Generic fallback for "thành phần"
    "thanh phan": categoryColors["Thành phần phức hợp"], // Without diacritics
    "lên men": categoryColors["Thành phần lên men"],
    "len men": categoryColors["Thành phần lên men"],
    "thực vật": categoryColors["Thành phần thực vật"],
    "thuc vat": categoryColors["Thành phần thực vật"],
    "dầu": categoryColors["Dầu"],
    "dau": categoryColors["Dầu"],
    "khoáng chất": categoryColors["Khoáng chất"],
    "khoang chat": categoryColors["Khoáng chất"],
    "làm trắng": categoryColors["Thành phần làm trắng"],
    "lam trang": categoryColors["Thành phần làm trắng"],
    "phức hợp": categoryColors["Thành phần phức hợp"],
    "phuc hop": categoryColors["Thành phần phức hợp"],
    "biển": categoryColors["Thành phần biển"],
    "bien": categoryColors["Thành phần biển"],
    "truyền thống": categoryColors["Thành phần truyền thống"],
    "truyen thong": categoryColors["Thành phần truyền thống"],
    "đặc biệt": categoryColors["Thành phần đặc biệt"],
    "dac biet": categoryColors["Thành phần đặc biệt"],
    "khác": categoryColors["Khác"],
    "khac": categoryColors["Khác"],
  };
  
  // Check English mappings
  for (const [key, color] of Object.entries(englishMappings)) {
    if (lowerCategory.includes(key)) {
      return color;
    }
  }
  
  // Check Vietnamese mappings
  for (const [key, color] of Object.entries(vietnameseMappings)) {
    if (lowerCategory.includes(key)) {
      return color;
    }
  }
  
  // Default fallback
  return categoryColors["other"];
};

const OccurrenceChart = ({ data }) => {
  const { translate } = useLanguage();
  const [zoom, setZoom] = useState(1);
  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const width = 500;
  const height = 500;

  const { primary, secondary } = data;

  const handleNodeClick = useCallback((nodeId) => {
    setSelectedNode(prevSelected => prevSelected === nodeId ? null : nodeId);
  }, []);

  const handleNodeHover = useCallback((nodeId, isEnter) => {
    setHoveredNode(isEnter ? nodeId : null);
  }, []);

  const isNodeConnectedToSelected = useCallback((nodeId, networkData) => {
    if (!selectedNode) return false;
    return networkData.links.some(link => {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;
      return (sourceId === selectedNode && targetId === nodeId) ||
             (targetId === selectedNode && sourceId === nodeId);
    });
  }, [selectedNode]);

  const isNodeConnectedToHovered = useCallback((nodeId, networkData) => {
    if (!hoveredNode) return false;
    return networkData.links.some(link => {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;
      return (sourceId === hoveredNode && targetId === nodeId) ||
             (targetId === hoveredNode && sourceId === nodeId);
    });
  }, [hoveredNode]);

  const getFilteredNetworkData = useCallback((networkData, searchTerm) => {
    if (!networkData) return { nodes: [], links: [] };
    if (!searchTerm) return networkData;

    const term = searchTerm.toLowerCase();
    
    // 1. 검색어와 직접 일치하는 노드 찾기
    const directMatchNodes = new Set(
      networkData.nodes
        .filter(node => node.id.toLowerCase().includes(term))
        .map(node => node.id)
    );

    // 2. 직접 일치하는 노드와 연결된 노드 찾기
    const connectedNodes = new Set();
    networkData.links.forEach(link => {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;
      
      if (directMatchNodes.has(sourceId)) {
        connectedNodes.add(targetId);
      }
      if (directMatchNodes.has(targetId)) {
        connectedNodes.add(sourceId);
      }
    });

    // 3. 모든 관련 노드 합치기
    const allRelevantNodes = new Set([...directMatchNodes, ...connectedNodes]);

    // 4. 필터링된 노드와 링크 생성
    const filteredNodes = networkData.nodes.filter(node => 
      allRelevantNodes.has(node.id)
    );

    const filteredLinks = networkData.links.filter(link => {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;
      return allRelevantNodes.has(sourceId) && allRelevantNodes.has(targetId);
    });

    return {
      nodes: filteredNodes,
      links: filteredLinks
    };
  }, []);

  const shouldShowLink = useCallback((link, networkData) => {
    const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
    const targetId = typeof link.target === 'object' ? link.target.id : link.target;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const isSourceMatch = sourceId.toLowerCase().includes(term);
      const isTargetMatch = targetId.toLowerCase().includes(term);
      
      // 검색어와 직접 일치하거나 연결된 노드들의 링크 표시
      if (isSourceMatch || isTargetMatch) return true;
      
      // 검색어와 일치하는 노드와 연결된 모든 링크 표시
      const isConnectedToSearch = networkData.links.some(l => {
        const sId = typeof l.source === 'object' ? l.source.id : l.source;
        const tId = typeof l.target === 'object' ? l.target.id : l.target;
        return (sId.toLowerCase().includes(term) || tId.toLowerCase().includes(term)) &&
               (sourceId === sId || sourceId === tId || targetId === sId || targetId === tId);
      });
      
      return isConnectedToSearch;
    }

    if (selectedNode) {
      return sourceId === selectedNode || targetId === selectedNode;
    }

    if (hoveredNode) {
      return sourceId === hoveredNode || targetId === hoveredNode;
    }

    return false;
  }, [selectedNode, hoveredNode, searchTerm]);


  const getNodeSize = (node) => {
    if (!node || typeof node.size === 'undefined') {
      return 20; // 기본 크기 반환
    }
    // 최소 크기 15, 최대 크기 40으로 설정
    return Math.max(15, Math.min(40, 15 + node.size * 0.5));
  };

  const getLinkStrength = (value) => {
    // 링크 값에 따른 선 굵기 계산
    const minWidth = 1;
    const maxWidth = 4;
    const normalizedValue = Math.min(Math.max(value, 1), 10); // 1-10 사이로 정규화
    return minWidth + ((normalizedValue - 1) / 9) * (maxWidth - minWidth);
  };

  const getCoOccurringIngredients = (nodeId, networkData) => {
    return networkData.links
      .filter(link => {
        const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
        const targetId = typeof link.target === 'object' ? link.target.id : link.target;
        return sourceId === nodeId || targetId === nodeId;
      })
      .map(link => {
        const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
        const targetId = typeof link.target === 'object' ? link.target.id : link.target;
        return {
          id: sourceId === nodeId ? targetId : sourceId,
          value: link.value
        };
      })
      .sort((a, b) => b.value - a.value);
  };


  const calculateNodePositions = (networkData) => {
    const positions = {};
    const nodeCount = networkData.nodes.length;
    
    // 원형 레이아웃으로 노드 배치
    networkData.nodes.forEach((node, index) => {
      const angle = (2 * Math.PI * index) / nodeCount;
      const radius = Math.min(width, height) * 0.35;
      
      const x = width/2 + radius * Math.cos(angle);
      const y = height/2 + radius * Math.sin(angle);
      
      // 노드 위치 고정
      node.x = x;
      node.y = y;
      node.fx = x;
      node.fy = y;
      
      positions[node.id] = { x, y };
    });

    return positions;
  };

  const renderChart = (chartData, title, colorType = "primary") => {
    const originalNetworkData = processChartData(chartData);
    const networkData = getFilteredNetworkData(originalNetworkData, searchTerm);
    const nodePositions = calculateNodePositions(networkData);

    // 검색어와 일치하는 첫 번째 노드 찾기
    const searchMatchNode = searchTerm ? 
      networkData.nodes.find(node => 
        node.id.toLowerCase().includes(searchTerm.toLowerCase())
      ) : null;

    // 검색된 노드를 우선순위로 하고, 없을 경우 선택된 노드 표시
    const displayNode = searchMatchNode || 
      (selectedNode ? networkData.nodes.find(n => n.id === selectedNode) : null);

    // 카테고리별 노드 수 계산 (filtered data for legend)
    const categoryCount = networkData.nodes.reduce((acc, node) => {
      const category = node.category || 'other';
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});

    // 차트 영역 클릭 핸들러 추가
    const handleChartClick = (e) => {
      // SVG 요소나 차트 컨테이너를 직접 클릭했을 때만 선택 해제
      if (e.target.tagName === 'svg' || e.target.classList.contains('occurrence-network-container')) {
        setSelectedNode(null);
      }
    };

    return (
      <div className="occurrence-chart-container bbc-style">
        {title && (
          <div className="occurrence-chart-header">
            <span
              className="occurrence-metric-dot"
              style={{
                background: colorType === 'secondary' ? BBC_STYLE.SECONDARY_COLOR : BBC_STYLE.PRIMARY_COLOR
              }}
            />
            <h3 style={{
              fontSize: '0.85rem',
              fontWeight: '600',
              color: BBC_STYLE.TEXT_COLOR,
              margin: '0',
              lineHeight: '1.4',
              fontFamily: BBC_STYLE.FONT_FAMILY
            }}>
              {renderFilterLabel(title)}
            </h3>
          </div>
        )}

        <div className="occurrence-chart-content">
          {/* Legend on the left */}
          <div className="occurrence-legend">
            <h4 className="occurrence-legend-title">
              {translate('trendPage.ingredientAnalysis.occurrence.classification')}
            </h4>
            {Object.entries(categoryColors)
              .filter(([category]) => categoryCount[category] > 0)
              .map(([category, color]) => (
                <div key={category} className="occurrence-legend-item">
                  <div className="occurrence-color-dot" style={{ backgroundColor: color }} />
                  <span>{category}</span>
                  <span className="occurrence-count">({categoryCount[category] || 0})</span>
                </div>
              ))}
          </div>

          {/* Network SVG with popup overlay */}
          <div className="occurrence-network-wrapper">
            <div
              className="occurrence-network-container"
              onClick={handleChartClick}
            >
              <svg
                width="100%"
                height="100%"
                viewBox={`0 0 ${width} ${height}`}
                preserveAspectRatio="xMidYMid meet"
                onClick={handleChartClick}
              >
                <defs>
                  {Object.keys(categoryColors).map((category) => (
                    <radialGradient key={`gradient-${category}-${colorType}`} id={`gradient-${category}-${colorType}`}>
                      <stop offset="0%" stopColor={getCategoryColor(category)} stopOpacity="0.9" />
                      <stop offset="100%" stopColor={getCategoryColor(category)} stopOpacity="0.7" />
                    </radialGradient>
                  ))}
                </defs>

                {renderLinks(networkData, nodePositions, colorType)}
                {renderNodes(networkData, nodePositions, colorType)}
              </svg>
            </div>

            {/* Popup for selected node - appears on node click */}
            {displayNode && (
              <div className="occurrence-node-popup">
                <button
                  className="popup-close-btn"
                  onClick={() => setSelectedNode(null)}
                >
                  ×
                </button>
                <div className="popup-header">
                  <span
                    className="popup-ingredient-name"
                    style={{ color: getCategoryColor(displayNode.category) }}
                  >
                    {displayNode.id}
                  </span>
                  <span className="popup-category">{displayNode.category}</span>
                </div>
                <div className="popup-stats">
                  <div className="popup-stat">
                    <span className="popup-stat-value">{displayNode.size}</span>
                    <span className="popup-stat-label">{translate('trendPage.ingredientAnalysis.occurrence.frequency')}</span>
                  </div>
                  <div className="popup-stat">
                    <span className="popup-stat-value">{displayNode.degree}</span>
                    <span className="popup-stat-label">{translate('trendPage.ingredientAnalysis.occurrence.connections')}</span>
                  </div>
                </div>
                {getCoOccurringIngredients(displayNode.id, networkData).length > 0 && (
                  <div className="popup-connected">
                    <h5>{translate('trendPage.ingredientAnalysis.occurrence.connectedIngredients')}</h5>
                    <div className="popup-connected-list">
                      {getCoOccurringIngredients(displayNode.id, networkData).slice(0, 5).map(({ id, value }) => (
                        <div key={id} className="popup-connected-item">
                          <span>{id}</span>
                          <span className="popup-connected-value">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderNodes = (networkData, nodePositions, colorType) => {
    return networkData.nodes.map((node) => {
      const isSearchMatch = searchTerm && node.id.toLowerCase().includes(searchTerm.toLowerCase());
      const isConnectedToSearch = searchTerm && networkData.links.some(link => {
        const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
        const targetId = typeof link.target === 'object' ? link.target.id : link.target;
        const otherNodeId = sourceId === node.id ? targetId : sourceId;
        return otherNodeId.toLowerCase().includes(searchTerm.toLowerCase());
      });

      const isConnected = isNodeConnectedToSelected(node.id, networkData) || 
                         isNodeConnectedToHovered(node.id, networkData);
      const isActive = node.id === selectedNode || node.id === hoveredNode;
      
      const shouldHighlight = isActive || isConnected || isSearchMatch || isConnectedToSearch;
      const nodeOpacity = shouldHighlight ? 1 : 0.3;
      const nodeSize = getNodeSize(node);
      
      // 고정된 위치 사용
      const position = nodePositions[node.id] || { x: width/2, y: height/2 };

      return (
        <g
          key={node.id}
          transform={`translate(${position.x}, ${position.y})`}
          className={`occurrence-node ${isConnected ? 'connected' : ''} ${isActive ? 'active' : ''} ${isSearchMatch || isConnectedToSearch ? 'searched' : ''}`}
          onClick={() => handleNodeClick(node.id)}
          onMouseEnter={() => handleNodeHover(node.id, true)}
          onMouseLeave={() => handleNodeHover(node.id, false)}
          style={{ transition: 'none' }} // 트랜지션 효과 제거
        >
          <circle
            r={nodeSize}
            fill={`url(#gradient-${node.category || 'other'}-${colorType})`}
            opacity={nodeOpacity}
            className="node-circle"
            style={{ transition: 'none' }} // 트랜지션 효과 제거
          />
          <text
            className="occurrence-node-label"
            dy=".35em"
            textAnchor="middle"
            fontSize={11 * zoom}
            style={{
              opacity: nodeOpacity,
              fill: BBC_STYLE.TEXT_COLOR,
              fontWeight: shouldHighlight ? "500" : "400",
              fontFamily: BBC_STYLE.FONT_FAMILY,
              transition: 'none' // 트랜지션 효과 제거
            }}
          >
            {node.id}
          </text>
        </g>
      );
    });
  };

  const renderLinks = (networkData, nodePositions, colorType) => {
    return networkData.links.map((link, i) => {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;
      
      const sourcePos = nodePositions[sourceId] || { x: 0, y: 0 };
      const targetPos = nodePositions[targetId] || { x: 0, y: 0 };
      
      const isVisible = shouldShowLink(link, networkData);
      const isSearchRelated = searchTerm && (
        sourceId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        targetId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        networkData.links.some(l => {
          const sId = typeof l.source === 'object' ? l.source.id : l.source;
          const tId = typeof l.target === 'object' ? l.target.id : l.target;
          return (sId === sourceId || sId === targetId || tId === sourceId || tId === targetId) &&
                 (sId.toLowerCase().includes(searchTerm.toLowerCase()) || tId.toLowerCase().includes(searchTerm.toLowerCase()));
        })
      );

      const shouldDisplay = isVisible || isSearchRelated;
      const linkOpacity = shouldDisplay ? 0.7 : 0.12;

      const dx = targetPos.x - sourcePos.x;
      const dy = targetPos.y - sourcePos.y;
      const distance = Math.sqrt(dx * dx + dy * dy) || 1; // 0으로 나누는 것 방지
      
      const startX = sourcePos.x + (dx * getNodeSize(networkData.nodes.find(n => n.id === sourceId))) / distance;
      const startY = sourcePos.y + (dy * getNodeSize(networkData.nodes.find(n => n.id === sourceId))) / distance;
      const endX = targetPos.x - (dx * getNodeSize(networkData.nodes.find(n => n.id === targetId))) / distance;
      const endY = targetPos.y - (dy * getNodeSize(networkData.nodes.find(n => n.id === targetId))) / distance;
      
      const midX = (startX + endX) / 2;
      const midY = (startY + endY) / 2;

      return (
        <g key={`link-${i}`}
           className={`occurrence-link ${shouldDisplay ? 'connected' : ''} ${isSearchRelated ? 'searched' : ''}`}
           style={{ pointerEvents: 'none' }}
        >
          <line
            x1={startX}
            y1={startY}
            x2={endX}
            y2={endY}
            strokeWidth={shouldDisplay ? getLinkStrength(link.value) : 1}
            style={{ 
              opacity: linkOpacity,
              stroke: shouldDisplay ? (colorType === "primary" ? "#5D89F6" : "#FF848F") : "#94a3b8"
            }}
          />
          {shouldDisplay && (
            <g transform={`translate(${midX},${midY})`}>
              <rect
                x="-12"
                y="-10"
                width="24"
                height="20"
                rx="4"
                fill="white"
                opacity="0.95"
              />
              <text
                textAnchor="middle"
                dy=".35em"
                fontSize="10"
                fill={BBC_STYLE.TEXT_COLOR}
                fontWeight="400"
                style={{ fontFamily: BBC_STYLE.FONT_FAMILY }}
              >
                {link.value}
              </text>
            </g>
          )}
        </g>
      );
    });
  };

  if (!data?.primary?.co_occurrence_metrics) {
    return <ErrorMessage message={translate('trendPage.ingredientAnalysis.errors.noOccurrenceData')} />;
  }

  const hasSecondaryData = secondary?.co_occurrence_metrics !== undefined;

  // Calculate static network statistics (not affected by search)
  const calculateStaticNetworkStats = (chartData) => {
    const networkData = processChartData(chartData);
    if (!networkData || networkData.nodes.length === 0) return null;
    
    // Find most connected node
    const mostConnected = networkData.nodes.reduce((max, node) => 
      node.degree > (max?.degree || 0) ? node : max, null
    );
    
    // Calculate average connections
    const avgConnections = networkData.nodes.reduce((sum, node) => 
      sum + (node.degree || 0), 0) / networkData.nodes.length;
    
    return {
      totalNodes: networkData.nodes.length,
      totalLinks: networkData.links.length,
      mostConnected,
      avgConnections: avgConnections.toFixed(1)
    };
  };

  // Create static narratives for both filters
  const primaryStats = primary ? calculateStaticNetworkStats(primary) : null;
  const secondaryStats = hasSecondaryData ? calculateStaticNetworkStats(secondary) : null;

  return (
    <div className={`occurrence-wrapper bbc-style ${hasSecondaryData ? 'has-secondary' : ''}`}>
      {/* Narrative Section - Following Design Guide */}
      <div className="occurrence-narrative">
        {!hasSecondaryData ? (
          // Single mode
          <>
            <h2 className="narrative-title">
              {translate('trendPage.ingredientAnalysis.occurrence.keyHubRole')}{' '}
              <span
                className="highlight-ingredient"
                style={{
                  color: BBC_STYLE.HIGHLIGHT_COLOR,
                  borderBottom: `2px solid ${BBC_STYLE.HIGHLIGHT_COLOR}`
                }}
              >
                {primaryStats?.mostConnected?.id}
              </span>
            </h2>
            <div className="narrative-subtitle">
              <span className="stat-pill" style={{ background: `${BBC_STYLE.PRIMARY_COLOR}15` }}>
                {primaryStats?.totalNodes} {translate('trendPage.ingredientAnalysis.occurrence.ingredients')}
              </span>
              <span className="stat-divider">•</span>
              <span>{primaryStats?.mostConnected?.degree} {translate('trendPage.ingredientAnalysis.occurrence.connections')}</span>
            </div>
          </>
        ) : (
          // Comparison mode
          <>
            <h2 className="narrative-title">
              {translate('trendPage.ingredientAnalysis.narratives.filterComparison')}
            </h2>
            <div className="narrative-comparison">
              <div className="comparison-item">
                <div className="comparison-dot" style={{ background: BBC_STYLE.PRIMARY_COLOR }} />
                <span className="comparison-label">{renderFilterLabel(translate('trendPage.filters.set1'))}:</span>
                <span
                  className="highlight-ingredient"
                  style={{
                    color: BBC_STYLE.PRIMARY_COLOR,
                    borderBottom: `2px solid ${BBC_STYLE.PRIMARY_COLOR}`
                  }}
                >
                  {primaryStats?.mostConnected?.id}
                </span>
                <span className="comparison-category">({primaryStats?.mostConnected?.degree} {translate('trendPage.ingredientAnalysis.occurrence.connections')})</span>
              </div>
              <div className="comparison-item">
                <div className="comparison-dot" style={{ background: BBC_STYLE.SECONDARY_COLOR }} />
                <span className="comparison-label">{renderFilterLabel(translate('trendPage.filters.set2'))}:</span>
                <span
                  className="highlight-ingredient"
                  style={{
                    color: BBC_STYLE.SECONDARY_COLOR,
                    borderBottom: `2px solid ${BBC_STYLE.SECONDARY_COLOR}`
                  }}
                >
                  {secondaryStats?.mostConnected?.id}
                </span>
                <span className="comparison-category">({secondaryStats?.mostConnected?.degree} {translate('trendPage.ingredientAnalysis.occurrence.connections')})</span>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="occurrence-chart-controls">
        <div className="occurrence-search-container">
          <Search className="occurrence-search-icon" />
          <input
            type="text"
            placeholder={translate('trendPage.ingredientAnalysis.occurrence.searchPlaceholder')}
            className="occurrence-search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="occurrence-zoom-controls">
          <button
            onClick={() => setZoom(z => Math.max(0.5, z - 0.1))}
            data-tooltip={translate('trendPage.ingredientAnalysis.occurrence.zoomOut')}
            disabled={zoom <= 0.5}
          >
            <ZoomOut />
          </button>
          <button
            onClick={() => setZoom(1)}
            data-tooltip={translate('trendPage.ingredientAnalysis.occurrence.reset')}
          >
            <RotateCcw />
          </button>
          <button
            onClick={() => setZoom(z => Math.min(2, z + 0.1))}
            data-tooltip={translate('trendPage.ingredientAnalysis.occurrence.zoomIn')}
            disabled={zoom >= 2}
          >
            <ZoomIn />
          </button>
        </div>
      </div>

      <div className="occurrence-charts-wrapper">
        {renderChart(primary, translate('trendPage.filters.set1'), "primary")}
        {hasSecondaryData && renderChart(secondary, translate('trendPage.filters.set2'), "secondary")}
      </div>
    </div>
  );
};

export default memo(OccurrenceChart);
